import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { RtI18nDirective } from '../../apps/shared/runtime-translation.directive';
import { OktaAuthService } from '../../../services/okta-auth.service';

/**
 * Shown when Okta authentication succeeded but the app could not complete sign-in - most
 * commonly because the backend has no Core.Users record to link the Okta account to.
 *
 * Before this existed, LoginCallbackComponent navigated to '/error', which had no route and
 * fell through the wildcard back to the login screen. The user saw their login "do nothing"
 * and had no idea why.
 */
@Component({
  standalone: true,
  templateUrl: './login-error.component.html',
  styleUrl: './login-error.component.scss',
  imports: [RtI18nDirective]
})
export class LoginErrorComponent implements OnInit {

  public message: string;

  // true = "no account / wrong login type" (see LoginCallbackComponent). Drives which retry action
  // the template shows: a full Okta sign-out (this case) vs a light retry (transient errors).
  public isProfileNotFound: boolean = false;

  public retryClicked: boolean = false;

  // The token capture kicked off in ngOnInit, before clearUnusableSession() empties the tokenManager.
  // Held as the promise (not the resolved value) so clickCleanRestart() can await it: Angular does not
  // await an async ngOnInit, so the button can render before the capture resolves, and signing out
  // without the captured idToken would fail to end the Okta SSO session. Only set for isProfileNotFound.
  private capturedTokensPromise?: Promise<{ idToken: any; accessToken: any; refreshToken: any }>;

  public constructor(private router: Router,
    private oktaAuthService: OktaAuthService) { }

  public async ngOnInit(): Promise<void> {
    // Passed by LoginCallbackComponent via navigation state.
    const state = this.router.getCurrentNavigation()?.extras.state
      ?? history.state;

    this.message = state?.['message']
      || 'We signed you in, but could not open your account. Please contact your school administrator.';

    this.isProfileNotFound = !!state?.['isProfileNotFound'];

    // For the "no account / wrong login type" case, capture the tokens BEFORE clearing the local
    // session - clearUnusableSession() empties the tokenManager, but clickCleanRestart() needs the
    // idToken to build Okta's /logout redirect. Kick it off and await it here so the capture reads the
    // tokens before the clear below; clickCleanRestart() re-awaits the held promise.
    if (this.isProfileNotFound) {
      this.capturedTokensPromise = this.oktaAuthService.captureTokensForSignOut();
      await this.capturedTokensPromise;
    }

    // The tokens are valid but unusable without a linked profile. Clearing them stops the app
    // from dropping into an empty /mainmenu on the next launch, and lets Retry start clean.
    // On native this must also clear the Keychain / EncryptedSharedPreferences copies, which is
    // why it goes through clearUnusableSession rather than tokenManager.clear(). This does NOT end
    // the Okta SSO session - that only happens if the user chooses clickCleanRestart().
    this.oktaAuthService.clearUnusableSession('Login succeeded but no profile could be linked')
      .catch(error => console.error('[LoginError] Failed to clear session after failed login:', error));
  }

  // Transient/system error: light retry. The Okta SSO session is intact, so this silently
  // re-authenticates - which is what we want here: once the transient issue clears, the retry
  // succeeds without forcing the user to re-enter a password.
  public async clickRetry(): Promise<void> {
    this.retryClicked = true;
    await this.router.navigate(['']);
  }

  // No account / wrong login type: end the Okta SSO session so the next attempt lands on a real login
  // prompt and the user can pick a different login type or account. Redirects to Okta's /logout.
  public async clickCleanRestart(): Promise<void> {
    this.retryClicked = true;
    // Await the capture started in ngOnInit so a fast click can't sign out with no idToken (which
    // wouldn't end the Okta SSO session). Falls back to empty tokens only if the capture never ran.
    const captured = (await this.capturedTokensPromise)
      ?? { idToken: undefined, accessToken: undefined, refreshToken: undefined };
    await this.oktaAuthService.endOktaSsoSession(captured);
  }
}
